import React, { useState } from 'react';
import { AlertTriangle, GripVertical, MessageSquare, MoreVertical, Pause } from 'lucide-react';
import { Badge } from '../ui/badge';
import { cn } from '../../lib/utils';
import WorkflowStepContextMenu from './WorkflowStepContextMenu';
import ExecutionPathStepBadge, { getPathBorderClass } from './ExecutionPathStepBadge';
import { getWorkflowStepCardClasses } from '../../utils/workflowStepCardStyles';
import { WORKFLOW_MAIN_COLUMN_WIDTH } from '../../utils/workflowCardAnchors';

const FloatingCardButton = ({ children, className, testId, ...props }) => (
  <button
    type="button"
    data-workflow-card-action
    data-workflow-editor-ignore-pan
    className={cn(
      'flex h-7 w-7 cursor-pointer items-center justify-center rounded-md border border-slate-200 bg-white text-slate-600 shadow-sm transition-colors hover:bg-slate-50 hover:text-slate-900',
      className,
    )}
    onPointerDown={(event) => event.stopPropagation()}
    onClick={(event) => event.stopPropagation()}
    data-testid={testId}
    {...props}
  >
    {children}
  </button>
);

const WorkflowStepCard = ({
  step,
  stepConfig,
  title,
  typeLabel,
  description,
  requiresBackend = false,
  hasChannelIssue = false,
  channelIssueMessage,
  isMoving = false,
  isSelected = false,
  highlighted = false,
  pathState = null,
  readOnly = false,
  hasClipboard = false,
  onEdit,
  onRemove,
  onDragHandleStart,
  onContextMenuAction,
  columnWidth = WORKFLOW_MAIN_COLUMN_WIDTH,
  isBranchStep = false,
}) => {
  const [isDragging, setIsDragging] = useState(false);
  const Icon = stepConfig?.icon;
  const styles = getWorkflowStepCardClasses({
    step,
    isSelected,
    isMoving,
    highlighted,
    pathState,
    hasChannelIssue,
  });
  const showHoverChrome = !readOnly && (isSelected || isMoving || highlighted);

  const handleCardBodyClick = (event) => {
    if (readOnly) return;
    if (event.target.closest('[data-workflow-card-action]')) return;
    onEdit?.(step);
  };

  return (
    <div
      className="group relative mx-auto flex w-full justify-center"
      style={{ width: columnWidth }}
      data-workflow-step-card
      data-workflow-branch-step-card={isBranchStep ? 'true' : undefined}
      data-workflow-card
      data-workflow-canvas-node
      data-step-id={step.id}
      data-workflow-card-center-x={columnWidth / 2}
      data-workflow-connector-anchor-top={`${columnWidth / 2},0`}
    >
      <ExecutionPathStepBadge state={pathState} />

      {!readOnly ? (
        <button
          type="button"
          aria-label="Move action"
          draggable
          data-workflow-drag-handle
          data-workflow-editor-ignore-pan
          onDragStart={(event) => {
            event.stopPropagation();
            event.dataTransfer.effectAllowed = 'move';
            setIsDragging(true);
            onDragHandleStart?.(step.id);
          }}
          onDragEnd={() => setIsDragging(false)}
          onPointerDown={(event) => event.stopPropagation()}
          onClick={(event) => event.stopPropagation()}
          className={cn(
            'absolute -left-8 top-1/2 z-30 flex h-8 w-8 -translate-y-1/2 cursor-grab items-center justify-center rounded-md border border-transparent bg-white text-slate-400 opacity-0 shadow-sm transition-opacity active:cursor-grabbing group-hover:opacity-100 hover:border-slate-200 hover:text-slate-600',
            (showHoverChrome || isDragging) && 'opacity-100',
          )}
          data-testid="step-drag-handle"
        >
          <GripVertical size={16} />
        </button>
      ) : null}

      {!readOnly ? (
        <div
          className={cn(
            'absolute -top-9 right-0 z-30 flex items-center gap-1 opacity-0 transition-opacity group-hover:opacity-100',
            showHoverChrome && 'opacity-100',
          )}
        >
          <FloatingCardButton
            aria-label="Pause action"
            testId="step-pause-button"
            onClick={() => onContextMenuAction?.('pause', step.id)}
          >
            <Pause size={14} />
          </FloatingCardButton>
          <FloatingCardButton
            aria-label="Add note"
            testId="step-note-button"
            onClick={() => onContextMenuAction?.('notes', step.id)}
          >
            <MessageSquare size={14} />
          </FloatingCardButton>
        </div>
      ) : null}

      <div
        role="button"
        tabIndex={readOnly ? -1 : 0}
        onClick={handleCardBodyClick}
        onKeyDown={(event) => {
          if (readOnly) return;
          if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            onEdit?.(step);
          }
        }}
        className={cn(
          styles.card,
          readOnly ? 'cursor-default' : 'cursor-pointer',
          pathState && getPathBorderClass(pathState),
        )}
        data-workflow-card-body
        data-testid={`step-node-${step.id}`}
      >
        <div
          className={cn(
            'flex h-11 w-11 flex-shrink-0 items-center justify-center rounded-xl',
            styles.icon,
          )}
        >
          {Icon ? <Icon size={20} /> : null}
        </div>

        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <p className={cn('truncate text-sm font-semibold', styles.title)}>{title}</p>
            {requiresBackend && (
              <Badge
                variant="outline"
                className={cn('text-[10px] font-medium', styles.badge)}
                data-testid="backend-required-badge"
              >
                Backend required
              </Badge>
            )}
            {hasChannelIssue && (
              <Badge variant="outline" className="border-red-200 bg-red-100 text-[10px] text-red-700">
                <AlertTriangle size={10} className="mr-1" />
                Channel Issue
              </Badge>
            )}
          </div>
          {typeLabel ? (
            <p className={cn('text-xs font-medium', styles.description)}>{typeLabel}</p>
          ) : null}
          <p className={cn('truncate text-xs', styles.description)}>{description}</p>
          {hasChannelIssue && channelIssueMessage ? (
            <p className="mt-1 text-xs text-red-600">{channelIssueMessage}</p>
          ) : null}
        </div>

        {!readOnly ? (
          <WorkflowStepContextMenu
            hasClipboard={hasClipboard}
            onAction={(action) => onContextMenuAction?.(action, step.id)}
          >
            <button
              type="button"
              data-workflow-card-action
              data-workflow-editor-ignore-pan
              onPointerDown={(event) => event.stopPropagation()}
              onClick={(event) => event.stopPropagation()}
              aria-label="More action options"
              className="cursor-pointer rounded-md p-1.5 text-slate-500 hover:bg-slate-100"
              data-testid="step-context-menu-trigger"
            >
              <MoreVertical size={16} />
            </button>
          </WorkflowStepContextMenu>
        ) : null}
      </div>
    </div>
  );
};

export default WorkflowStepCard;
